const CART_SNAPSHOT_VERSION = 1;
const MAX_SAVED_LINES = 100;

export const CART_SNAPSHOT_KEY = 'spark-cart-persistence-v1';
export const CHECKOUT_MARKER_KEY = 'spark-cart-checkout-started-v1';

export function createCartSnapshot(cart, savedAt = Date.now()) {
  const items = Array.isArray(cart?.items)
    ? cart.items.slice(0, MAX_SAVED_LINES).map(normalizeCartLine).filter(Boolean)
    : [];

  return {
    version: CART_SNAPSHOT_VERSION,
    savedAt,
    cartToken: String(cart?.token || ''),
    items,
  };
}

export function captureCartSnapshot(storage, cart, savedAt = Date.now()) {
  const snapshot = createCartSnapshot(cart, savedAt);

  if (!snapshot.items.length) {
    storage.removeItem(CART_SNAPSHOT_KEY);
    return null;
  }

  storage.setItem(CART_SNAPSHOT_KEY, JSON.stringify(snapshot));
  return snapshot;
}

export function cartPersistenceDecision({ cart, snapshot, checkoutMarker }) {
  if (Array.isArray(cart?.items) && cart.items.length > 0) return 'capture';
  if (!isValidSnapshot(snapshot)) return 'none';
  if (checkoutMarker) return 'clear-after-checkout';
  return 'restore';
}

export async function synchronizePersistentCart({
  storage,
  fetchImpl,
  cartUrl,
  cartAddUrl,
  reload,
  now = Date.now,
}) {
  const cart = await fetchCart(fetchImpl, cartUrl);
  const snapshot = readJson(storage, CART_SNAPSHOT_KEY);
  const checkoutMarker = readJson(storage, CHECKOUT_MARKER_KEY);
  const decision = cartPersistenceDecision({ cart, snapshot, checkoutMarker });

  if (decision === 'capture') {
    captureCartSnapshot(storage, cart, now());
    storage.removeItem(CHECKOUT_MARKER_KEY);
    return 'captured';
  }

  if (decision === 'clear-after-checkout') {
    storage.removeItem(CART_SNAPSHOT_KEY);
    storage.removeItem(CHECKOUT_MARKER_KEY);
    return 'cleared-after-checkout';
  }

  if (decision !== 'restore') return 'empty';

  const response = await fetchImpl(cartAddUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ items: snapshot.items }),
  });

  if (!response.ok) {
    throw new Error('Shopify could not restore the saved cart.');
  }

  const restoredCart = await fetchCart(fetchImpl, cartUrl);
  captureCartSnapshot(storage, restoredCart, now());
  storage.removeItem(CHECKOUT_MARKER_KEY);
  reload();
  return 'restored';
}

export async function captureCurrentShopifyCart({ storage, fetchImpl, cartUrl, now = Date.now }) {
  const cart = await fetchCart(fetchImpl, cartUrl);
  captureCartSnapshot(storage, cart, now());
  return cart;
}

export function markCheckoutStarted(storage, now = Date.now) {
  const snapshot = readJson(storage, CART_SNAPSHOT_KEY);
  if (!isValidSnapshot(snapshot)) return;

  storage.setItem(
    CHECKOUT_MARKER_KEY,
    JSON.stringify({
      startedAt: now(),
      cartToken: snapshot.cartToken,
    })
  );
}

function normalizeCartLine(item) {
  const id = Number(item?.id);
  const quantity = Math.floor(Number(item?.quantity));
  if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) return null;

  const line = { id, quantity };
  const properties = normalizeProperties(item?.properties);
  const sellingPlan = Number(item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan || 0);

  if (Object.keys(properties).length) line.properties = properties;
  if (Number.isSafeInteger(sellingPlan) && sellingPlan > 0) line.selling_plan = sellingPlan;

  return line;
}

function normalizeProperties(properties) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};

  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key, value]) => key && value !== null && value !== undefined)
      .map(([key, value]) => [String(key), String(value)])
  );
}

function isValidSnapshot(snapshot) {
  return (
    snapshot?.version === CART_SNAPSHOT_VERSION &&
    Array.isArray(snapshot.items) &&
    snapshot.items.length > 0 &&
    snapshot.items.every((item) => normalizeCartLine(item))
  );
}

function readJson(storage, key) {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (_) {
    storage.removeItem(key);
    return null;
  }
}

async function fetchCart(fetchImpl, cartUrl) {
  const response = await fetchImpl(cartUrl, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error('Shopify cart could not be loaded.');
  return response.json();
}

function initializeCartPersistence() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const storage = window.localStorage;
  const root = String(window.Shopify?.routes?.root || '/');
  const cartUrl = `${root}cart.js`;
  const cartAddUrl = `${root}cart/add.js`;
  const fetchImpl = window.fetch.bind(window);
  const capture = () =>
    captureCurrentShopifyCart({ storage, fetchImpl, cartUrl }).catch((error) => {
      console.warn('[SparkStickers] Saved cart could not be updated.', error);
    });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    Promise.resolve(event.promise)
      .then(capture)
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[SparkStickers] Cart update could not be saved.', error);
        }
      });
  });

  document.addEventListener('spark:cart-updated', (event) => {
    captureCartSnapshot(storage, event.detail?.cart);
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]')) return;
      markCheckoutStarted(storage);
    },
    true
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) capture();
  });

  synchronizePersistentCart({
    storage,
    fetchImpl,
    cartUrl,
    cartAddUrl,
    reload: () => window.location.reload(),
  }).catch((error) => {
    console.warn('[SparkStickers] Saved cart could not be restored.', error);
  });
}

initializeCartPersistence();
